import { AdminGuard } from "@/admin/components/admin-guard";
import { birthdayLabel, type SelfEvent } from "@/admin/lib/birthday-label";
import { UserAvatar } from "@/components/user-avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CopyButton } from "@/components/ui/copy-button";
import { QueryError } from "@/components/ui/query-error";
import { RelativeTime } from "@/components/ui/relative-time";
import { Input } from "@/components/ui/input";
import { SearchInput } from "@/components/ui/search-input";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { EmptyState } from "@/components/ui/empty-state";
import { Skeleton } from "@/components/ui/skeleton";
import { SortableHead } from "@/components/ui/sortable-head";
import { useClientSort } from "@/hooks/use-client-sort";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { api, edenFetch } from "@/lib/eden";
import { formatDate } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Users, X } from "lucide-react";
import { useEffect, useState } from "react";
import type { ChangeEvent } from "react";

export interface SearchForm {
  q: string;
  last_name: string;
  first_name: string;
  birthday: string;
  premium_only: boolean;
}

function errorDetail(err: unknown): string {
  const e = err as { value?: { detail?: string }; detail?: string; message?: string };
  return e?.value?.detail ?? e?.detail ?? e?.message ?? "Search failed";
}

const EMPTY_FORM: SearchForm = {
  q: "",
  last_name: "",
  first_name: "",
  birthday: "",
  premium_only: false,
};
function isBlank(f: SearchForm): boolean {
  return !(f.q.trim() || f.last_name.trim() || f.first_name.trim() || f.birthday || f.premium_only);
}

export type PremiumStatus = "trial" | "active" | "grace" | "ended" | "free";
/** Same words as the member page's billing tab (BDAY-377) — support reads both. */
const PREMIUM_LABEL: Record<PremiumStatus, string> = {
  trial: "Free trial",
  active: "Active",
  grace: "Payment failed (extra time)",
  ended: "Ended",
  free: "Free",
};
const PREMIUM_SORT: Record<PremiumStatus, number> = {
  trial: 0,
  active: 1,
  grace: 2,
  ended: 3,
  free: 4,
};

export function PremiumCell({ premium }: { premium: AdminUserRow["premium"] }) {
  const variant =
    premium.status === "active" || premium.status === "trial"
      ? "default"
      : premium.status === "grace"
        ? "destructive"
        : "outline";
  return (
    <div className="flex flex-col gap-0.5">
      <Badge variant={variant} className="w-fit text-xs">
        {PREMIUM_LABEL[premium.status]}
      </Badge>
      {premium.status === "trial" && premium.until && (
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          ends {formatDate(premium.until)}
        </span>
      )}
    </div>
  );
}

export type StatusTab =
  | "all"
  | "active"
  | "locked"
  | "pending_deletion"
  | "deleted"
  | "admins"
  | "test";

export interface AdminUserRow {
  user_id: string;
  user_no: string;
  username: string;
  email: string | null;
  display_name: string | null;
  avatar_url: string | null;
  user_status_code: string;
  is_admin: boolean;
  is_banned: boolean;
  is_email_verified: boolean;
  auth_provider: string;
  signup_country_code: string | null;
  last_active_at: string | null;
  joined_at: string | null;
  created_at: string;
  deleted_at: string | null;
  locked_at: string | null;
  scheduled_deletion_at: string | null;
  anonymized_at: string | null;
  merged_into_user_id: string | null;
  legacy_member_id: string | null;
  is_test_user: boolean;
  emails: string[];
  birthday: SelfEvent | null;
  premium: { status: PremiumStatus; until: string | null };
}

const STATUS_TABS: StatusTab[] = [
  "all",
  "active",
  "locked",
  "pending_deletion",
  "deleted",
  "admins",
  "test",
];
export function isStatusTab(value: unknown): value is StatusTab {
  return STATUS_TABS.includes(value as StatusTab);
}

/**
 * The users list query. Exported because user-detail re-runs the very same one to work
 * out a member's Previous / Next — same key, so it usually answers from cache.
 */
export function usersListQuery(tab: StatusTab, filters: SearchForm) {
  return {
    queryKey: ["/api/admin/users", tab, filters],
    queryFn: async () => {
      const res = await edenFetch(
        api.api.admin.users.get({
          query: {
            status: tab,
            q: filters.q.trim() || undefined,
            last_name: filters.last_name.trim() || undefined,
            first_name: filters.first_name.trim() || undefined,
            birthday: filters.birthday || undefined,
            premium_only: filters.premium_only ? "1" : undefined,
          },
        }),
      );
      return res as { users: AdminUserRow[] };
    },
    retry: false,
  };
}

/** The status badge a row shows — and what the Status column sorts on. */
export function statusBadge(u: AdminUserRow): {
  label: string;
  variant: "destructive" | "outline" | "secondary";
} {
  if (u.anonymized_at) return { label: "anonymized", variant: "outline" };
  if (u.deleted_at) return { label: "deleted", variant: "outline" };
  if (u.is_banned) return { label: "banned", variant: "destructive" };
  if (u.locked_at) return { label: "locked", variant: "destructive" };
  return { label: u.user_status_code, variant: "secondary" };
}

function StatusCell({ u }: { u: AdminUserRow }) {
  const status = statusBadge(u);
  return (
    <div className="flex flex-wrap gap-1">
      <Badge variant={status.variant} className="text-xs">
        {status.label}
      </Badge>
      {u.merged_into_user_id && (
        <Badge variant="outline" className="text-xs">
          merged
        </Badge>
      )}
      {u.is_test_user && (
        <Badge variant="outline" className="text-xs">
          test
        </Badge>
      )}
    </div>
  );
}

const USER_SORT_COLUMNS = {
  name: { value: (u: AdminUserRow) => u.display_name || u.username },
  email: { value: (u: AdminUserRow) => u.emails[0] ?? u.email },
  premium: { value: (u: AdminUserRow) => PREMIUM_SORT[u.premium.status] },
  birthday: {
    value: (u: AdminUserRow) =>
      u.birthday ? u.birthday.event_month! * 100 + u.birthday.event_day! : null,
  },
  status: { value: (u: AdminUserRow) => statusBadge(u).label },
  joined: { value: (u: AdminUserRow) => u.joined_at, firstDir: "desc" as const },
  last_active: { value: (u: AdminUserRow) => u.last_active_at, firstDir: "desc" as const },
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/admin/users/" });
  const tab: StatusTab = isStatusTab(search.tab) ? search.tab : "active";

  // Legacy admin member-search parity (2026-09-23): member # / email, or last name, is the
  // anchor; first name, birthday and premium-only are filters on top (server enforces).
  // The form is local while typing; the debounced copy is what the URL carries, so a
  // refresh or Back reproduces the same search.
  const fromSearch = (): SearchForm => ({
    q: search.q ?? "",
    last_name: search.last_name ?? "",
    first_name: search.first_name ?? "",
    birthday: search.birthday ?? "",
    premium_only: !!search.premium_only,
  });
  const [form, setForm] = useState<SearchForm>(fromSearch);
  const [debounced, setDebounced] = useState(form);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(form), 400);
    return () => clearTimeout(t);
  }, [form]);
  useEffect(() => {
    const next = {
      q: debounced.q.trim() || undefined,
      last_name: debounced.last_name.trim() || undefined,
      first_name: debounced.first_name.trim() || undefined,
      birthday: debounced.birthday || undefined,
      premium_only: debounced.premium_only || undefined,
    };
    // A same-URL replace on mount raced the non-admin redirect off /admin into a render loop.
    const same = (Object.keys(next) as (keyof typeof next)[]).every((k) => next[k] === search[k]);
    if (same) return;
    void navigate({
      from: "/admin/users/",
      to: "/admin/users",
      search: (prev) => ({ ...prev, ...next }),
      replace: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);
  useEffect(() => {
    const next = fromSearch();
    // Only adopt when the URL really differs from what's typed (ignoring the trailing
    // space the trim dropped), or the round-trip eats the space mid-word.
    const trimmed = (f: SearchForm): SearchForm => ({
      ...f,
      q: f.q.trim(),
      last_name: f.last_name.trim(),
      first_name: f.first_name.trim(),
    });
    setForm((f) => (JSON.stringify(trimmed(f)) === JSON.stringify(next) ? f : next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.q, search.last_name, search.first_name, search.birthday, search.premium_only]);

  const set = (patch: Partial<SearchForm>) => setForm((f) => ({ ...f, ...patch }));
  // Clear resets every field at once and skips the debounce, so the full list is back
  // on the click rather than 400ms later (BDAY-379).
  const clearForm = () => {
    setForm(EMPTY_FORM);
    setDebounced(EMPTY_FORM);
  };
  const canClear = !isBlank(form) || !isBlank(debounced);
  const searching = !isBlank(debounced);

  const { data, isLoading, error, refetch } = useQuery(usersListQuery(tab, debounced));
  const errorText = error ? errorDetail(error) : null;

  const users = data?.users ?? [];
  // Click-to-sort is local: the list is capped server-side and has no pager, so sorting
  // what's on screen is the whole list. It deliberately does NOT travel in the URL —
  // user-detail's Previous / Next walks the server order instead.
  const { sorted, sortKey, sortDir, onSort } = useClientSort(users, USER_SORT_COLUMNS);

  // What the row links hand the detail page so it can rebuild this list for its
  // Previous / Next. Prefixed `l*` to stay clear of the detail's own `tab`/`grant`.
  const listSearch = {
    lq: search.q,
    lln: search.last_name,
    lfn: search.first_name,
    lbd: search.birthday,
    lpo: search.premium_only,
    ltab: tab,
  };

  return (
    <AdminGuard>
      <div className="flex flex-col gap-6 p-6 max-w-7xl">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6" />
          <h1 className="text-2xl font-semibold">Users</h1>
          {!isLoading && <span className="text-sm text-muted-foreground">({users.length})</span>}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <SegmentedControl<StatusTab>
            variant="underline"
            value={tab}
            onValueChange={(value) => {
              void navigate({
                to: "/admin/users",
                search: (prev) => ({ ...prev, tab: value }),
              });
            }}
            segments={[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "locked", label: "Locked" },
              { key: "pending_deletion", label: "Pending deletion" },
              { key: "deleted", label: "Deleted" },
              { key: "admins", label: "Admins" },
              { key: "test", label: "Test" },
            ]}
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <SearchInput
            value={form.q}
            onChange={(e: ChangeEvent<HTMLInputElement>) => set({ q: e.target.value })}
            placeholder="Member # / email (exact, or ≥5-char prefix) / username"
            containerClassName="w-80"
          />
          <Input
            value={form.last_name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => set({ last_name: e.target.value })}
            placeholder="Last name (word start, ≥3)"
            className="w-52"
          />
          <Input
            value={form.first_name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => set({ first_name: e.target.value })}
            placeholder="First name (word start)"
            className="w-44"
          />
          <Input
            type="date"
            value={form.birthday}
            onChange={(e: ChangeEvent<HTMLInputElement>) => set({ birthday: e.target.value })}
            title="Birthday (month + day are matched)"
            className="w-40"
          />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={form.premium_only}
              onCheckedChange={(v) => set({ premium_only: v === true })}
            />
            Premium only
          </label>
          {canClear && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearForm}
              data-testid="button-clear-search"
            >
              <X className="size-4" />
              Clear
            </Button>
          )}
        </div>
        {errorText ? (
          <QueryError
            message={`Couldn't load users: ${errorText}`}
            onRetry={() => void refetch()}
          />
        ) : isLoading ? (
          <div className="space-y-2">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : users.length === 0 ? (
          <EmptyState
            icon="users"
            title="No users found"
            message={searching ? "No users match your search." : "No users in this view yet."}
          />
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHead
                    label="User"
                    sortKey="name"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    testId="th-user-name"
                  />
                  <TableHead>User #</TableHead>
                  <SortableHead
                    label="Email"
                    sortKey="email"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    testId="th-user-email"
                  />
                  <SortableHead
                    label="Premium"
                    sortKey="premium"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    testId="th-user-premium"
                  />
                  <SortableHead
                    label="Birthday"
                    sortKey="birthday"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    testId="th-user-birthday"
                  />
                  <SortableHead
                    label="Status"
                    sortKey="status"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    testId="th-user-status"
                  />
                  <SortableHead
                    label="Joined"
                    sortKey="joined"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    testId="th-user-joined"
                  />
                  <SortableHead
                    label="Last active"
                    sortKey="last_active"
                    activeKey={sortKey}
                    dir={sortDir}
                    onSort={onSort}
                    testId="th-user-last-active"
                  />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sorted.map((u) => (
                  <TableRow
                    key={u.user_id}
                    className="cursor-pointer hover:bg-muted/40"
                    onClick={() =>
                      void navigate({
                        to: "/admin/users/$id",
                        params: { id: u.user_id },
                        search: listSearch,
                      })
                    }
                  >
                    <TableCell>
                      <Link
                        to="/admin/users/$id"
                        params={{ id: u.user_id }}
                        search={listSearch}
                        className="flex items-center gap-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <UserAvatar
                          avatarUrl={u.avatar_url}
                          name={u.display_name || u.username}
                          className="w-8 h-8"
                        />
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate flex items-center gap-2">
                            {u.display_name || u.username}
                            {u.is_admin && (
                              <Badge variant="secondary" className="text-xs">
                                admin
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            @{u.username}
                          </div>
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell className="text-sm font-mono text-muted-foreground">
                      <CopyButton value={String(u.user_no)} label="Copy member number">
                        {u.user_no}
                      </CopyButton>
                      {u.legacy_member_id && (
                        <span title="Migrated from legacy" className="ml-1 text-xs">
                          ⚙
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {/* Every live address, primary first, one per line (BDAY-378). */}
                      <div className="flex flex-col gap-0.5">
                        {(u.emails.length ? u.emails : u.email ? [u.email] : []).map((email, i) => (
                          <div key={email} className="flex items-center gap-2">
                            <CopyButton value={email} label="Copy email">
                              <span className="truncate">{email}</span>
                            </CopyButton>
                            {i === 0 && u.is_email_verified && (
                              <Badge variant="outline" className="text-xs">
                                verified
                              </Badge>
                            )}
                          </div>
                        ))}
                        {u.emails.length === 0 && !u.email && <span>—</span>}
                      </div>
                    </TableCell>
                    <TableCell>
                      <PremiumCell premium={u.premium} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {birthdayLabel(u.birthday) ?? "—"}
                    </TableCell>
                    <TableCell>
                      <StatusCell u={u} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {u.joined_at ? formatDate(u.joined_at) : "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <RelativeTime value={u.last_active_at} fallback="—" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </AdminGuard>
  );
}
